import { Directive, inject } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Control } from './alabama/alabama-model';
import { UimuiBaseRulesService } from './services/uimui-base-rules.service';
import { NotificationService } from '../../_services/notification/notification.service';
import { UserService } from '../../_services/user.service';
import { LimitType, UserRole } from '../../config/constants';
import { LoadingService } from 'axis-components';
import { startWith } from 'rxjs';
import { RequestService } from '../../_services/request/request.service';

@Directive()
export class UimuiBaseStateComponent<BaseRules, TextValues> {
  reactiveForm: FormGroup;
  dynamicRules = [];
  baseRules: BaseRules;
  controls: Control[];
  textValues: TextValues;
  isReviewPage: boolean = false;
  selectedPremium: any;
  state: string;
  showPremium: boolean = false;
  static stopLoading: boolean = false;
  static loadedStatesCount: number = 0;
  static totalStatesCount: number = 0;
  public baseRulesService = inject(UimuiBaseRulesService);
  private loadingService = inject(LoadingService);
  outstandingLoadingService: any;
  getPdfData: any;
  hasFirstFormChange: boolean = false;
  public userService = inject(UserService);
  public notificationService = inject(NotificationService);
  public baseRequestService = inject(RequestService);

  getRadioButtons(control: any) {
    return !this.baseRules[control.baseRuleKey]?.includes('show');
  }

  private getLabelBySelectionId(
    labelMap: Record<string, string> | undefined,
    selectionId: string,
  ): string | undefined {
    if (
      !labelMap ||
      typeof labelMap !== 'object' ||
      Array.isArray(labelMap) ||
      typeof selectionId !== 'string' ||
      !selectionId
    ) {
      return undefined;
    }

    if (labelMap[selectionId]) {
      return labelMap[selectionId];
    }

    const wildcardKey = Object.keys(labelMap).find(
      (key) => key.endsWith('*') && selectionId.startsWith(key.slice(0, -1)),
    );

    return wildcardKey ? labelMap[wildcardKey] : undefined;
  }

  private applySelectionIdBasedLabels(): void {
    const selectionId = (this.baseRules as any)?.selectionId;
    if (
      typeof selectionId !== 'string' ||
      !selectionId ||
      !Array.isArray(this.controls)
    ) {
      return;
    }

    for (const control of this.controls) {
      const defaultControlLabel =
        (control as any).__defaultLabel ?? control.label;
      (control as any).__defaultLabel = defaultControlLabel;

      const controlLabel =
        this.getLabelBySelectionId(control.labelBySelectionId, selectionId) ??
        defaultControlLabel;

      if (controlLabel !== undefined) {
        control.label = controlLabel;
      }

      if (!Array.isArray(control.option)) {
        continue;
      }

      for (const option of control.option) {
        const defaultOptionLabel =
          (option as any).__defaultLabel ?? option.label;
        (option as any).__defaultLabel = defaultOptionLabel;

        const optionLabel =
          this.getLabelBySelectionId(option.labelBySelectionId, selectionId) ??
          defaultOptionLabel;

        if (optionLabel !== undefined) {
          option.label = optionLabel;
        }
      }
    }
  }

  setupControlEvents() {
    if (this.baseRules?.['hasOutstanding']) {
      this.setValidation();
    }
    this.reactiveForm.valueChanges.subscribe((res) => {
      const controls = Object.values(this.reactiveForm.controls);

      // Check if any control is valid and has been interacted with
      const anyValid = controls.some(
        (control: any) => control.valid && (control.dirty || control.touched),
      );
      // Only log on the first change
      if (
        !this.notificationService.UMUIMOutstandingNotificationFlag &&
        this.userService.role === UserRole.BROKER &&
        anyValid
      ) {
        this.notificationService.UMUIMOutstandingNotificationFlag = true;
        this.notificationService.showNotification(
          'Please note that the insured must fill out this form using the link sent to their inbox. All changes made to this view will not be saved.',
          'warning',
          'Selections Will Not Be Saved',
        );
      }
    });

    this.controls.forEach((control: Control) => {
      if (control.hasEvent) {
        let isInitialLoad = true;
        this.reactiveForm
          .get(control.formControlName)
          .valueChanges.pipe(
            startWith(this.reactiveForm.get(control.formControlName).value),
          )
          .subscribe((res) => {
            if (res && !this.baseRulesService.isContinueButtonClicked) {
              const hasExistingValue =
                (res?.value !== undefined && res?.value !== null) ||
                (typeof res === 'string' && res !== null);
              if (hasExistingValue) {
                this.eventHook(res.value ?? res);
                // Only reset during user interactions, not during initial load/navigation
                if (!isInitialLoad) {
                  this.setDynamicRules(res.value ?? res);
                }
              }
            }
            // Set flag to false after first emission (initial load)
            isInitialLoad = false;
          });
      }
    });
  }

  eventHook(value: any) {}

  getJsonRuleFiles(state: string, formGroup: FormGroup) {
    this.reactiveForm = formGroup;
    this.state = state;
    //Initialize counts on first call (reset for new loading cycle)
    if (UimuiBaseStateComponent.loadedStatesCount === 0) {
      UimuiBaseStateComponent.stopLoading = false;
      UimuiBaseStateComponent.totalStatesCount =
        this.baseRulesService.getApplicableStates().length;
    }
    this.baseRulesService.getAllRules(state).subscribe((allRules) => {
      const baseRuleQuery = this.baseRuleQueryBuilder();

      if (this.baseRulesService.savedBaseRules[state]) {
        this.baseRules = this.baseRulesService.savedBaseRules[state];
      } else {
        this.baseRules = this.baseRulesService.getRuleBySelectionId(
          allRules.baseRules,
          baseRuleQuery,
        );
      }

      // Temporary fix for liability limit summary page break
      // Don't load the state if baseRules is undefined (only for review/summary pages without formGroup)
      if (!this.baseRules && !formGroup) {
        UimuiBaseStateComponent.checkAndUpdateLoadingProgress(state);
        return;
      }

      this.textValues = allRules.textValues;
      this.controls = allRules.controls;
      this.applySelectionIdBasedLabels();
      if (formGroup) {
        this.dynamicRules = this.baseRulesService.getRuleBySelectionId(
          allRules.dynamicRules,
          baseRuleQuery,
        )?.option;
        this.setupControlEvents();
      } else {
        this.isReviewPage = true;
      }

      // Only initialize PDF data dump if baseRules exists
      if (this.baseRules) {
        this.baseRulesService.initialPDFDataDumpProp(
          this.controls,
          allRules.state,
        );

        this.baseRulesService.getPdfDataDumpPropForSummaryPage(
          this.baseRules,
          allRules.state,
        );
      }

      this.setupHookForDropDownData();

      // Check and update loading progress
      UimuiBaseStateComponent.checkAndUpdateLoadingProgress(state);

      const loaderService =
        this.outstandingLoadingService || this.loadingService;
      if (
        UimuiBaseStateComponent.loadedStatesCount >=
        UimuiBaseStateComponent.totalStatesCount
      ) {
        loaderService.stop();
      }
    });
  }
  setupHookForDropDownData() {
    this.controls = [
      ...this.controls.map((control) => {
        if (this.shouldMapPolicyLiabilityLimit(control)) {
          control = {
            ...control,
            data: [this.getPolicyLiabilityLimitOption()],
          };
        } else if (control.hasHookForData) {
          control = { ...control, data: [this.calculatePremium(control)] };
        }
        return control;
      }),
    ];
  }

  private shouldMapPolicyLiabilityLimit(control: Control): boolean {
    const shouldSetPolicyLiabilityLimit =
      this.baseRules?.['setPolicyLiabilityLimit'] === true;
    const usePolicyLiabilityLimit = control.usePolicyLiabilityLimit === true;

    if (!shouldSetPolicyLiabilityLimit || !usePolicyLiabilityLimit) {
      return false;
    }

    const baseRuleValue = this.baseRules?.[control.baseRuleKey];
    return (
      typeof baseRuleValue === 'string' &&
      (baseRuleValue.includes('show') || baseRuleValue.includes('readonly'))
    );
  }

  private getPolicyLiabilityLimitOption(): { id: number; name: string } {
    // For Massachusetts: use MA policy if exists, otherwise fall back to Policy 1
    // For other states: use only their respective policy (Policy 1)
    let liabilityLimit: string;
    if (this.state === 'massachusetts') {
      const maLiability = this.baseRequestService.getLiabilityLimitForState('massachusetts');
      liabilityLimit = maLiability || this.baseRequestService.getLiabilityLimitForState(this.state);
    } else {
      liabilityLimit = this.baseRequestService.getLiabilityLimitForState(this.state);
    }

    return {
      id: 1,
      name: liabilityLimit.split('.')[0],
    };
  }

  calculatePremium(c: Control): any {
    return [];
  }

  baseRuleQueryBuilder() {
    if (this.baseRulesService.getNonGarageStates().includes(this.state)) {
      return this.baseRulesService.states.find((s) => s.state === this.state)
        ?.defaultQuery;
    }
    return undefined;
  }

  static resetLoadingState() {
    UimuiBaseStateComponent.stopLoading = false;
    UimuiBaseStateComponent.loadedStatesCount = 0;
    UimuiBaseStateComponent.totalStatesCount = 0;
  }

  static checkAndUpdateLoadingProgress(stateName: string) {
    // Increment loaded states count
    UimuiBaseStateComponent.loadedStatesCount++;
    // Check if all states are loaded
    if (
      UimuiBaseStateComponent.loadedStatesCount >=
      UimuiBaseStateComponent.totalStatesCount
    ) {
      UimuiBaseStateComponent.stopLoading = true;
    }
  }

  setValidation() {
    // Temporary fix for liability limit summary page break
    if (!this.baseRules) return;

    const requiredRadios = Array.from(
      new Set(
        Object.entries(this.baseRules)
          .filter(
            ([key, value]) => key.endsWith('_Radio') && value.includes('show'),
          )
          .map(([key]) => {
            const match = key.match(/_(.+_Radio)$/);
            return match ? match[1] : key;
          }),
      ),
    );

    const requiredDropDowns = Array.from(
      new Set(
        Object.entries(this.baseRules)
          .filter(
            ([key, value]) =>
              key.endsWith('_DropDown') &&
              !value.includes('optional') &&
              (value.includes('show') || value.includes('form')),
          )
          .map(([key]) => key),
      ),
    );
    const requiredInputs = Array.from(
      new Set(
        Object.entries(this.baseRules).filter(
          ([key, value]) => key.endsWith('_Input') && value.includes('show'),
        ),
      ),
    );
    requiredInputs.forEach((input) => {
      const control = this.reactiveForm.get(input);
      if (control) {
        control.setValidators([Validators.required]);
      }
    });
    requiredRadios.forEach((radio) => {
      const radioControlName = radio.charAt(0).toLowerCase() + radio.slice(1);
      const control = this.reactiveForm.get(radioControlName);
      if (control) {
        control.setValidators([Validators.required]);
      }
    });
    requiredDropDowns.forEach((dropDown) => {
      const control = this.reactiveForm.get(dropDown);
      if (control) {
        control.setValidators([Validators.required]);
      }
    });

    this.reactiveForm.updateValueAndValidity();
  }

  setDynamicRules(control: string) {
    this.baseRules = {
      ...this.baseRules,
      ...this.dynamicRules.find((option) => option.select === control)
        ?.changeBaseRules,
    };
    this.baseRulesService.savedBaseRules[this.state] = this.baseRules;

    const resetControls =
      this.dynamicRules.find((option) => option.select === control)
        ?.resetControls || [];

    // Reset conflicting controls to prevent PDF conflicts
    resetControls.forEach((controlName) => {
      const formControl = this.reactiveForm.get(controlName);
      if (formControl) {
        formControl.clearValidators();
        formControl.reset();
        this.resetPremium();
      }
    });

    // Handle dynamic label changes
    this.applyDynamicLabelChanges(control);

    this.setValidation();
    this.setupHookForDropDownData();
  }

  /**
   * Apply dynamic label changes based on the selected control
   * Override this method in state components to customize label change behavior
   * @param control - The selected control value
   */
  protected applyDynamicLabelChanges(control: string): void {
    const changeDynamicLabel =
      this.dynamicRules.find((option) => option.select === control)
        ?.changeDynamicLabel || {};

    Object.keys(changeDynamicLabel).forEach((controlName) => {
      const controlObj = this.controls.find(
        (ctrl) => ctrl.formControlName === controlName,
      );
      if (controlObj) {
        controlObj.label = changeDynamicLabel[controlName];
      }
    });
  }

  private resetPremium() {
    this.controls.forEach((controlJsonObj) => {
      if (
        controlJsonObj.formControlName.toLocaleLowerCase().includes('dropdown')
      ) {
        const controlObj = this.reactiveForm.get(
          controlJsonObj.formControlName,
        );
        if (controlObj.value === null) {
          controlJsonObj.selectedPremium = '';
        }
      }
    });
  }

  /**
   * Centralized premium calculation for policy liability limits
   * @param control - The control to calculate premium for
   * @param liabilityLimit - The liability limit value
   * @param lookupControlName - The name of the control to lookup premium data from
   * @returns The calculated premium or empty string if not found
   */
  protected calculatePolicyLiabilityPremium(
    control: Control,
    liabilityLimit: string,
    lookupControlName?: string,
  ): string {
    if (!lookupControlName) {
      return '';
    }

    const lookupControl = this.controls.find(
      (c) => c.formControlName === lookupControlName,
    );

    let premium = '';
    if (lookupControl?.data) {
      // Find first premium where limit >= liability limit
      for (const item of lookupControl.data) {
        // Allow state components to exclude items based on condition
        if (this.shouldExcludeSplitLimitItem(item, lookupControlName)) {
          continue;
        }

        if (
          this.baseRulesService.getNumberFromString(item.name) >=
          this.baseRulesService.getNumberFromString(liabilityLimit)
        ) {
          premium = this.getItemPremium(item, control);
          break;
        }
      }
      // If no match found, find the highest non-empty premium value
      if (!premium && lookupControl.data.length > 0) {
        for (let i = lookupControl.data.length - 1; i >= 0; i--) {
          const dataItem = lookupControl.data[i];

          // Allow state components to exclude items based on condition
          if (this.shouldExcludeSplitLimitItem(dataItem, lookupControlName)) {
            continue;
          }

          const premiumValue = this.getItemPremium(dataItem, control);
          if (premiumValue) {
            premium = premiumValue;
            break;
          }
        }
      }
    }

    // Store premium in PDF data dump
    this.storePremiumInPdfDump(control, premium, this.state);

    return premium;
  }

  /**
   * Override this method in state components to provide state-specific lookup control names
   * @param control - The control to get lookup name for
   * @returns The lookup control name or undefined
   */
  protected getLookupControlName(control: Control): string | undefined {
    // Default implementation - override in state components
    const limitTypeKey = LimitType.CombinedSingleLimit.replaceAll(' ', '');
    const isUIM = control.formControlName.includes('UIM');

    return isUIM
      ? 'UIM' + limitTypeKey + '_DropDown'
      : 'UM' + limitTypeKey + '_DropDown';
  }

  /**
   * Override this method in state components to exclude split limit items during premium calculation
   * @param dataItem - The limit data item to check
   * @param lookupControlName - The control name to determine filtering logic
   * @returns true if the split limit item should be excluded, false to include it
   */
  protected shouldExcludeSplitLimitItem(
    dataItem: any,
    lookupControlName: string,
  ): boolean {
    // Default implementation - include all split limit items
    return false;
  }

  /**
   * Override this method in state components to customize how premium is stored in PDF dump
   * @param control - The control being processed
   * @param premium - The calculated premium value
   */
  protected storePremiumInPdfDump(
    control: Control,
    premium: string,
    state: string,
  ): void {
    // Default implementation - override in state components if needed
    const pdfKey = this.getPdfPremiumKey(control);

    this.baseRulesService.pdfDataDump[state] = {
      ...this.baseRulesService.pdfDataDump[state],
      [pdfKey]: premium,
    };
  }

  handleDropdownSelection(control: Control, event: any): void {
    if (!event) {
      control.selectedPremium = '';
      return;
    }

    const premiumKey = this.getSelectedPremiumKey(control);
    const selectedPremium =
      event.premium ?? (premiumKey ? event[premiumKey] : undefined);

    // Check if the selected option has no premium (empty string)
    // This typically means "Limit equal to the limit of my Liability Coverage" was selected
    if (
      selectedPremium === '' ||
      selectedPremium === null ||
      selectedPremium === undefined
    ) {
      // Calculate premium based on liability limit
      let liabilityLimit = this.baseRequestService
        .getLiabilityLimitForState(this.state)
        .split('.')[0];
      if (event.name.includes('double the policy liability limit')) {
        const doubledLimit =
          this.baseRulesService.getNumberFromString(liabilityLimit) * 2;
        liabilityLimit = doubledLimit.toString();
      }

      const lookupControlName = this.getLookupControlName(control);
      const calculatedPremium = this.calculatePolicyLiabilityPremium(
        control,
        liabilityLimit,
        lookupControlName,
      );

      control.selectedPremium = calculatedPremium;
    } else {
      // Use the premium from the selected option
      control.selectedPremium = selectedPremium;
    }
  }

  /**
   * Override this method in state components when dropdown payload uses
   * a premium field name other than `premium`.
   */
  protected getSelectedPremiumKey(control: Control): string | undefined {
    return 'premium';
  }

  /**
   * Returns the premium value from a dropdown data item.
   * Override in state components when premium fields vary by selection
   * (for example stacked vs non-stacked).
   */
  protected getItemPremium(item: any, control?: Control): string {
    return item?.premium ?? '';
  }

  /**
   * Returns the PDF dump key for liability premium based on coverage type
   * (UIM/UM). Override in state component if custom mapping is needed.
   */
  protected getPdfPremiumKey(control: Control): string {
    const isUIM =
      control.formControlName.includes('UIM') ||
      control.formControlName.includes('UnderInsured');

    return isUIM ? 'liabilityPremiumUIM' : 'liabilityPremiumUM';
  }

  setupHookForLiabilityDropDownData() {
    this.controls = [
      ...this.controls.map((control) => {
        if (this.shouldMapPolicyLiabilityLimit(control)) {
          const liabilityLimit = this.baseRequestService
            .getLiabilityLimitForState(this.state)
            .split('.')[0];

          // Check if premium calculation is needed
          const shouldCalculatePremium =
            this.baseRulesService.selectionTypeKey === 'PolicyLiabilityLimits';

          if (shouldCalculatePremium) {
            // Get lookup control name from state component (override this method to provide state-specific names)
            const lookupControlName = this.getLookupControlName(control);
            const premium = this.calculatePolicyLiabilityPremium(
              control,
              liabilityLimit,
              lookupControlName,
            );

            control = {
              ...control,
              data: [
                {
                  id: 1,
                  name: liabilityLimit,
                  premium: premium,
                },
              ],
            };
          } else {
            control = {
              ...control,
              data: [this.getPolicyLiabilityLimitOption()],
            };
          }
        } else if (control.hasHookForData) {
          control = { ...control, data: [this.calculatePremium(control)] };
        }
        return control;
      }),
    ];
  }
}
